import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { postToast } from 'features/common/utils/postFlash';

import { PunchupTablePage } from 'features/common/components/Punchup/pages/PunchupTablePage';
import { PunchupSearch } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupSearch';
import { PunchupErrorState } from 'features/common/components/Punchup/primitives/feedback/PunchupErrorState';
import { PunchupAutoRefreshToggle } from 'features/common/components/Punchup/composites/forms/PunchupAutoRefreshToggle';
import { PunchupScansTable } from './PunchupScansTable';
import { PunchupTablePageHeader } from 'features/common/components/Punchup/composites/tables/PunchupTablePageHeader';
import { useDebounce } from 'utils/utils';
import { isBackgroundRefetch } from 'features/common/utils/queryStatus';
import { useAutoRefresh } from 'features/common/hooks/useAutoRefresh';
import { useScansPage } from './hooks/useScansPage';

export const PunchupScannedTickets: FC = () => {
	const { t } = useTranslation();
	const { autoRefresh, setAutoRefresh, refetchInterval } = useAutoRefresh();

	const {
		scans,
		hasSeats,
		isSearching,
		isLoading,
		isFetching,
		isError,
		hasData,
		refetch,
		setSearchText,
		sortBy,
		order,
		handleSort,
	} = useScansPage({ refetchInterval });
	const searchDebounce = useDebounce(setSearchText);

	// A manual Refresh dims the table with a spinner overlay; auto-refresh polls silently
	// (no dim flicker every minute while the manager watches the door — the toggle being on
	// is the live-mode cue). The in-button Refresh spinner still reflects either fetch.
	const refetchOverlay = isBackgroundRefetch({ isFetching, isLoading }) && !autoRefresh;

	// On a failed FIRST load the table body would otherwise render the "No scans yet" empty copy —
	// which misreads a failure as an empty room. Show a persistent, retryable panel instead; a
	// background-refetch error (data already present) keeps the table + the toast below.
	const errorFallback =
		isError && !hasData ? (
			<PunchupErrorState
				title={t('scans-load-error-title', "We couldn't load scanned tickets")}
				description={t('scans-load-error-description', 'Something went wrong fetching the scan log for this show.')}
				onRetry={() => refetch()}
				retryLabel={t('button-try-again', 'Try again')}
			/>
		) : undefined;

	useEffect(() => {
		if (isError) postToast('error', t('scans-load-error', 'Failed to load scanned tickets data'));
	}, [isError, t]);

	return (
		<PunchupTablePage
			pageName="Scanned Tickets"
			header={
				<PunchupTablePageHeader
					title="Scanned Tickets"
					description="Every scanned ticket — scan time, attendee, and ticket type, newest first."
					onRefresh={() => refetch()}
					refreshLabel={t('scans-refresh', 'Refresh')}
					isRefreshing={isFetching}
				/>
			}
			search={
				<PunchupSearch
					onChange={(e) => searchDebounce(e.target.value)}
					placeholder={t('scans-search-placeholder', 'Search by name or email')}
				/>
			}
			controlsActions={<PunchupAutoRefreshToggle checked={autoRefresh} onChange={setAutoRefresh} />}
			pageFallback={errorFallback}
		>
			<PunchupScansTable
				scans={scans}
				hasSeats={hasSeats}
				isSearching={isSearching}
				isLoading={isLoading}
				sortBy={sortBy}
				order={order}
				onSort={handleSort}
				isRefetching={refetchOverlay}
			/>
		</PunchupTablePage>
	);
};
